import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Product, PlayableItem, ShopExtended } from '../../domain/types';
import { catalogService } from '../../domain/services/catalogService';
import { partnerService } from '../../domain/services/partnerService';
import { useCartActions } from '../../state/CartContext';
import { useFavorites } from '../../state/FavoritesContext';
import { useToast } from '../../state/ToastContext';
import { useAuth } from '../../state/auth';
import { useAudio } from '../../state/AudioContext';
import { HorizontalProductList } from '../../components/HorizontalProductList';
import { ProductAudioPlayer } from '../../components/ProductAudioPlayer';
import { ProductImageHeader } from './components/ProductImageHeader';
import { ProductStickyBar } from './components/ProductStickyBar';
import { ProductQuestions } from './components/ProductQuestions';
import { ProductReviews } from './components/ProductReviews';
import { APP_CONFIG, STOCK, DISPLAY_LIMITS, PRICING, FINANCE, DEFAULTS, UI_ANIMATIONS } from '../../domain/constants';
import { BILLING } from '../../domain/constants/billing';
import { pricingService } from '../../domain/services/pricingService';
import { logger } from '../../infrastructure/logger';
import { ProductSkeleton } from '../../components/skeletons/ProductSkeleton';
import { StoreBadgeGroup, StoreType } from '../../components/ui/StoreBadge';

export const ProductDetailScreen = () => {
     const navigate = useNavigate();
     const { id } = useParams<{ id: string }>(); 
     const { addToCart } = useCartActions();
     const { toggleFavorite, isFavorite } = useFavorites();
     const { user } = useAuth();
     const { addToast } = useToast();
     const { stop, currentItem } = useAudio();

     // 1. Data State
     const [product, setProduct] = useState<Product | null>(null);
     const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
     const [shopProducts, setShopProducts] = useState<Product[]>([]);
     const [shop, setShop] = useState<ShopExtended | null>(null);
     const [loading, setLoading] = useState(true);
     
      // 2. UI State
      const [quantity, setQuantity] = useState<number>(DEFAULTS.PRODUCT_QUANTITY);
     const [isAdded, setIsAdded] = useState(false);

     // 3. Derived State (Memoized for performance)
     const isFav = useMemo(() => id ? isFavorite(id) : false, [id, isFavorite]);
      const installmentPrice = useMemo(() => product ? ((product.price || 0) / FINANCE.INSTALLMENT_COUNT).toLocaleString('es-AR', {maximumFractionDigits: 0}) : '0', [product]);
     const clubPrice = useMemo(() => product ? pricingService.calculateClubPrice(product.price || 0) : 0, [product]);
     const isProLocked = useMemo(() => !!(product?.isPro && !user), [product, user]);
     const isOutOfStock = useMemo(() => !!(product?.stock === 0), [product]);
      const isLowStock = useMemo(() => (product?.stock || 0) > 0 && (product?.stock || 0) <= STOCK.LOW_STOCK_THRESHOLD, [product]);

     const audioItem: PlayableItem = useMemo(() => product ? ({
         id: product.id,
         title: product.title,
         imageUrl: product.imageUrl,
         contentToRead: `${product.subtitle || ''}. Precio: ${(product.price || 0).toLocaleString('es-AR')} pesos. ${product.description}.`,
         type: 'product'
     }) : { id: '', title: '', imageUrl: '', contentToRead: '', type: 'product' }, [product]);

     const conditionInfo = useMemo(() => {
         if (!product) return { label: 'Nuevo', color: 'bg-black/5' };
         switch(product.condition) {
             case 'like-new': return { label: 'Como Nuevo', color: 'bg-green-100 text-green-700' };
             case 'good': return { label: 'Usado - Bueno', color: 'bg-yellow-100 text-yellow-700' };
             case 'fair': return { label: 'Con detalles', color: 'bg-orange-100 text-orange-700' };
             default: return { label: 'Nuevo', color: 'bg-black/5 dark:bg-white/10 text-text-secondary dark:text-gray-300' };
         }
     }, [product]);

     // 4. Effects
     useEffect(() => {
         return () => {
             if (currentItem?.type === 'product') stop(); 
         };
     }, [stop, currentItem]);

// SCROLL PERFORMANCE: Use CSS Variables with throttle
useEffect(() => {
  let ticking = false;
  const handleScroll = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--scroll-y-prod', `${window.scrollY}px`);
        ticking = false;
      });
      ticking = true;
    }
  };
  window.addEventListener('scroll', handleScroll, { passive: true } as AddEventListenerOptions);
  return () => window.removeEventListener('scroll', handleScroll, { passive: true } as EventListenerOptions);
}, []);

      useEffect(() => {
         let isMounted = true;
         const loadProductData = async () => {
             if (!id) { setLoading(false); return; }
             setLoading(true);
             window.scrollTo(0, 0); 
             
              try {
                  const foundProduct = await catalogService.getProductById(id);
                  if (isMounted) {
                      if (foundProduct) {
                          setProduct(foundProduct);
                           const result = await catalogService.getProductsByCategory(foundProduct.category, null, DISPLAY_LIMITS.RELATED_PRODUCTS * 2);
                           setRelatedProducts(result.products.filter(p => p.id !== id).slice(0, DISPLAY_LIMITS.RELATED_PRODUCTS));

                          // Load shop info and products from same shop
                          if (foundProduct.shopId) {
                              const shopData = await partnerService.getShopById(foundProduct.shopId);
                              if (isMounted && shopData) {
                                  setShop(shopData);
                                  const shopProds = await catalogService.getProductsByShop(foundProduct.shopId);
                                  setShopProducts(shopProds.filter(p => p.id !== id).slice(0, DISPLAY_LIMITS.SHOP_PRODUCTS_PREVIEW));
                              }
                          }
                      } else {
                          setProduct(null);
                      }
                  }
             } catch (error) {
                 logger.error("[ProductDetail] LOAD ERROR", error instanceof Error ? error : new Error(String(error)));
                 addToast("Error al cargar producto", "error");
             } finally {
                 if (isMounted) setLoading(false);
             }
         };
         loadProductData();
         return () => { isMounted = false; };
      }, [id, addToast]);

     const handleBack = useCallback(() => {
         if (window.history.state && window.history.state.idx > 0) navigate(-1);
         else navigate('/home', { replace: true });
     }, [navigate]);

     const handleShare = useCallback(async () => {
         if (!product) return;
         const shareUrl = `${APP_CONFIG.DOMAIN_URL}/product/${product.id}`;
         const shareData = { title: product.title, text: `¡Mirá este producto en AllTuc! ${product.title}`, url: shareUrl };
         try {
             if (navigator.canShare && navigator.canShare(shareData)) await navigator.share(shareData);
             else {
                 await navigator.clipboard.writeText(shareUrl);
                 addToast('Enlace copiado', 'success');
             }
         } catch { addToast('No se pudo compartir', 'error'); }
     }, [product, addToast]);

     const handleAddToCart = useCallback(async () => {
         if (!product) return;
         if (isAdded) { navigate('/cart'); return; }
         if (product.isPro && !user) { 
            addToast("Inicia sesión para comprar este producto", "info");
            navigate('/login', { state: { from: `/product/${id}` } });
            return; 
         }
         
         const success = await addToCart(product, quantity);
         if (success) {
             addToast("Agregado al carrito", "success");
             setIsAdded(true);
              setTimeout(() => setIsAdded(false), UI_ANIMATIONS.ADD_TO_CART_RESET_MS);
         }
     }, [product, isAdded, user, quantity, addToCart, navigate, addToast, id]);

     if (loading) return (
         <div className="min-h-screen bg-background-light dark:bg-background-dark px-4 py-6">
             <ProductSkeleton />
         </div>
     );

     if (!product) return (
         <div className="min-h-screen bg-background-light dark:bg-background-dark flex flex-col items-center justify-center p-6 text-center space-y-4 animate-fade-in">
             <h2 className="text-xl font-bold">Producto no encontrado</h2>
             <button onClick={() => navigate('/home')} className="px-6 py-3 bg-primary text-white font-bold rounded-xl">Volver al Inicio</button>
         </div>
     );

     return (
        <article className="bg-background-light dark:bg-background-dark font-display text-text-main dark:text-white overflow-x-hidden antialiased pb-28 w-full md:max-w-xl lg:max-w-2xl mx-auto relative h-full min-h-screen animate-fade-in shadow-2xl border-x border-black/5 dark:border-white/5" itemScope itemType="http://schema.org/Product">
            <header className="fixed top-0 left-0 right-0 z-50 w-full md:max-w-xl lg:max-w-2xl mx-auto h-[74px] bg-white/5 dark:bg-black/10 backdrop-blur-xl border-b border-white/10 dark:border-white/5 shadow-sm transition-all duration-300">
                <div className="h-full px-4 grid grid-cols-[80px_1fr_80px] items-center">
                    <div className="flex justify-start"><button onClick={handleBack} className="flex size-10 items-center justify-center rounded-full bg-black/10 dark:bg-white/10 border border-white/10 text-white"><span className="material-symbols-outlined">arrow_back</span></button></div>
                    <div className="flex flex-col items-center justify-center min-w-0 pointer-events-none">
                        <div className="flex items-center gap-1 text-[9px] uppercase font-bold tracking-widest opacity-80"><span className="pointer-events-auto cursor-pointer" onClick={() => navigate('/home')}>Inicio</span><span className="material-symbols-outlined text-[10px]">chevron_right</span><span className="pointer-events-auto cursor-pointer truncate" onClick={() => navigate('/catalog', { state: { category: product.category } })}>{product.category}</span></div>
                        <h1 className="text-xs font-bold truncate w-full text-center mt-0.5">{product.title}</h1>
                    </div>
                    <div className="flex justify-end gap-1.5">{product && <ProductAudioPlayer item={audioItem} isCompact={true} />}<button onClick={handleShare} className="flex size-9 items-center justify-center rounded-full bg-black/10 dark:bg-white/10 border border-white/10 text-white"><span className="material-symbols-outlined text-[18px]">ios_share</span></button></div>
                </div>
            </header>

            <ProductImageHeader 
                imageUrl={product.imageUrl}
                images={product.images}
                title={product.title}
                isFav={isFav}
                toggleFavorite={() => id && toggleFavorite(id)}
                isOutOfStock={isOutOfStock}
            />

                 <main className="flex flex-col px-6 pt-8 pb-4 relative -mt-12 bg-background-light dark:bg-background-dark rounded-t-[32px] shadow-[0_-10px_40px_rgba(0,0,0,0.15)] dark:shadow-black/50 z-20 min-h-[50vh]">
                <div className="w-12 h-1.5 bg-black/10 dark:bg-white/10 rounded-full mx-auto mb-6 opacity-50"></div>
                <div className="flex flex-col gap-1 mb-4">
                     <div className="flex justify-between items-start gap-4">
                         <div className="flex-1">
                            <h2 className="text-2xl font-black leading-tight tracking-tight">{product.title}</h2>
                         </div>
                         <div className="flex flex-col items-end shrink-0">
        <div className="flex items-center gap-1 bg-yellow-400/10 px-2 py-1 rounded-lg border border-yellow-400/20"><span className="material-symbols-outlined text-[16px] filled text-yellow-500">star</span><span className="text-sm font-bold text-yellow-600 dark:text-yellow-400">{product.rating}</span></div>
        <button 
          onClick={() => navigate(`/product/${product.id}/reviews`)}
          className="text-[10px] text-text-secondary mt-1 hover:text-primary underline"
        >
          {product.reviews} reseñas
        </button>
                         </div>
                     </div>
                     <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide ${conditionInfo.color}`}>{conditionInfo.label}</span>
                        {product.subCategory && <span className="text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">{product.subCategory}</span>}
                        {isLowStock && <span className="text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide bg-red-100 text-red-600 animate-pulse">¡Últimos {product.stock}!</span>}
                     </div>
                </div>

                <div className="flex flex-col gap-3 mb-8 bg-surface-light dark:bg-surface-dark p-5 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full pointer-events-none"></div>
                    <div className="flex flex-col gap-1 relative z-10">
                                                {product.isPro ? (
                                                     <div className="flex flex-col">
                                                        <div className="flex items-baseline gap-2">
                                                            <span className="text-lg text-gray-400 font-bold line-through">$ {(product.price || 0).toLocaleString('es-AR')}</span>
                                                            <span className="text-xs text-text-secondary font-medium uppercase">Precio Regular</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-3xl font-black text-primary tracking-tight">$ {(clubPrice || 0).toLocaleString('es-AR')}</span>
                                                            <span className="bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">Club AllTuc</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-end justify-between">
                                                        <div>
                                                            <span className="text-3xl font-black tracking-tight">$ {(product.price || 0).toLocaleString('es-AR')}</span>
                                                            {product.price > PRICING.TRANSFER_DISCOUNT_THRESHOLD && <p className="text-xs text-green-600 font-bold mt-1">5% OFF por transferencia</p>}
                                                        </div>
                                                        {product.price > BILLING.SHIPPING.FREE_THRESHOLD && (
                                                            <div className="flex flex-col items-end">
                                                                <span className="material-symbols-outlined text-green-500 text-2xl">local_shipping</span>
                                                                <span className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wide">Envío Gratis</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}                    </div>
                    <div className="h-px w-full bg-black/5 dark:bg-white/5"></div>
                    <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0"><span className="material-symbols-outlined text-xl">credit_card</span></div><div><p className="text-sm font-medium">3 cuotas sin interés de <span className="font-bold">$ {installmentPrice}</span></p><p className="text-[10px] text-text-secondary dark:text-gray-400">Con todas las tarjetas bancarias</p></div></div>
                </div>

                <section className="mb-8">
                    <h3 className="text-lg font-bold mb-3">Sobre el producto</h3>
                    <div className="text-text-secondary dark:text-gray-300 text-sm leading-relaxed space-y-4">
                        <p>{product.description}</p>
                        <div className="grid grid-cols-2 gap-3 mt-4"><div className="bg-white dark:bg-surface-dark p-3 rounded-xl border border-black/5 dark:border-white/5"><span className="block text-[10px] uppercase font-bold text-text-secondary dark:text-gray-500 mb-1">Garantía</span><span className="text-sm font-bold">{product.condition !== 'new' ? '3 meses' : '6 meses oficial'}</span></div><div className="bg-white dark:bg-surface-dark p-3 rounded-xl border border-black/5 dark:border-white/5"><span className="block text-[10px] uppercase font-bold text-text-secondary dark:text-gray-500 mb-1">Stock</span><span className="text-sm font-bold">{product.stock} unidades</span></div></div>
                    </div>
                </section>
                 
                   <ProductQuestions
                       productId={product.id}
                       shopId={product.shopId}
                       partnerId={product.partnerId}
                   />

                   <ProductReviews
                       productId={product.id}
                       shopId={product.shopId}
                       partnerId={product.partnerId}
                   />

                   {shop && (
                       <div className="mt-6 mb-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
                           <div className="flex items-center justify-between">
                               <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/tienda/${shop.slug}`)}>
                                   {shop.assets?.logo ? (
                                       <img src={shop.assets.logo} alt={`Logo de ${shop.name}`} className="w-12 h-12 rounded-lg object-cover" />
                                   ) : (
                                       <div className="w-12 h-12 rounded-lg bg-orange-200 dark:bg-orange-800 flex items-center justify-center">
                                           <span className="material-symbols-outlined text-orange-600 dark:text-orange-400">storefront</span>
                                       </div>
                                   )}
                                   <div>
                                       <h3 className="font-bold text-text-main dark:text-white flex items-center gap-1">
                                           {shop.name}
                                       </h3>
                                       {/* Store Badge Group - Shows store type, performance and trust badges */}
                                       <div className="mt-1">
                                           <StoreBadgeGroup
                                               storeType={shop.id === 'alltuc-official' ? 'official' : (shop.isVerified ? 'verified' : 'basic')}
                                               isNew={shop.stats?.totalSales ? shop.stats.totalSales < 10 : false}
                                               isVerified={shop.isVerified}
                                               hasFastShipping={shop.stats?.totalSales ? shop.stats.totalSales > 50 : false}
                                               isTopRated={shop.stats?.rating ? shop.stats.rating >= 4.5 : false}
                                               hasGuarantee={shop.isVerified}
                                               showPerformance={true}
                                               showTrust={true}
                                               size="sm"
                                           />
                                       </div>
                                       <div className="flex items-center gap-1 text-xs text-text-secondary mt-1">
                                           <span className="material-symbols-outlined text-yellow-500 text-sm filled">star</span>
                                           <span>{(shop.stats?.rating || 0).toFixed(1)}</span>
                                           <span className="text-gray-400">({shop.stats?.reviewCount || 0} reseñas)</span>
                                       </div>
                                   </div>
                               </div>
                               <button 
                                   onClick={() => navigate(`/tienda/${shop.slug}`)}
                                   className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors"
                               >
                                   Ver Tienda
                               </button>
                           </div>
                       </div>
                   )}

                  {shopProducts.length > 0 && (
                      <div className="mb-6 -mx-6">
                          <HorizontalProductList 
                              title="Más de esta tienda" 
                              subtitle={`Otros productos de ${shop?.name || 'la tienda'}`}
                              products={shopProducts} 
                              isLoading={false} 
                              seeAllLink={shop ? `/tienda/${shop.slug}` : ''}
                              bgColor="bg-orange-50 dark:bg-orange-900/10"
                              titleColor="text-orange-600 dark:text-orange-400"
                              icon="storefront"
                          />
                      </div>
                  )}

                  <div className="h-px w-full bg-black/5 dark:bg-white/5 mb-6"></div>
                  <section className="mb-4 -mx-6"><HorizontalProductList title="Quizás te interese" products={relatedProducts} isLoading={false} seeAllLink="" /></section>
            </main>

            <ProductStickyBar 
                price={product.isPro ? clubPrice : (product.price || 0)}
                quantity={quantity}
                stock={product.stock || 0}
                setQuantity={setQuantity}
                onAdd={handleAddToCart}
                isAdded={isAdded}
                isProLocked={isProLocked}
                isOutOfStock={isOutOfStock}
                addToast={addToast}
            />
        </article>
);

     // Chat Widget - Only show if product and shop are available
     if (product && shop) {
         return (
             <>
                 <article className="max-w-2xl mx-auto px-6 pb-32">
                     <ProductImageHeader product={product} isProLocked={isProLocked} />
                     
                     <main className="space-y-6">
                         <div className="flex flex-col gap-4">
                             <div className="flex items-start justify-between gap-4">
                                 <div className="flex-1">
                                     <h1 className="text-2xl font-bold text-text-main dark:text-white mb-2">{product.name}</h1>
                                     <p className="text-sm text-text-secondary dark:text-gray-400 line-clamp-2">{product.shortDescription}</p>
                                 </div>
                             </div>
                             <div className="flex items-center gap-2">
                                 <div className="flex items-center gap-1 bg-yellow-400/10 px-2 py-1 rounded-lg border border-yellow-400/20"><span className="material-symbols-outlined text-[16px] filled text-yellow-500">star</span><span className="text-sm font-bold text-yellow-600 dark:text-yellow-400">{product.rating}</span></div>
                                 <button 
                                   onClick={() => navigate(`/product/${product.id}/reviews`)}
                                   className="text-[10px] text-text-secondary mt-1 hover:text-primary underline"
                                 >
                                   {product.reviews} reseñas
                                 </button>
                                   </div>
                               <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide ${conditionInfo.color}`}>{conditionInfo.label}</span>
                                  {product.subCategory && <span className="text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">{product.subCategory}</span>}
                                  {isLowStock && <span className="text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide bg-red-100 text-red-600 animate-pulse">¡Últimos {product.stock}!</span>}
                               </div>
                         </div>

                         <div className="flex flex-col gap-3 mb-8 bg-surface-light dark:bg-surface-dark p-5 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm relative overflow-hidden">
                             <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full pointer-events-none"></div>
                             <div className="flex flex-col gap-1 relative z-10">
                                                         {product.isPro ? (
                                                              <div className="flex flex-col">
                                                                 <div className="flex items-baseline gap-2">
                                                                     <span className="text-lg text-gray-400 font-bold line-through">$ {(product.price || 0).toLocaleString('es-AR')}</span>
                                                                     <span className="text-xs text-text-secondary font-medium uppercase">Precio Regular</span>
                                                                 </div>
                                                                 <div className="flex items-center gap-2 mt-1">
                                                                     <span className="text-3xl font-black text-primary tracking-tight">$ {(clubPrice || 0).toLocaleString('es-AR')}</span>
                                                                     <span className="bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">Club AllTuc</span>
                                                                 </div>
                                                             </div>
                                                         ) : (
                                                             <div className="flex items-end justify-between">
                                                                 <div>
                                                                     <span className="text-3xl font-black tracking-tight">$ {(product.price || 0).toLocaleString('es-AR')}</span>
                                                                     {product.price > PRICING.TRANSFER_DISCOUNT_THRESHOLD && <p className="text-xs text-green-600 font-bold mt-1">5% OFF por transferencia</p>}
                                                                 </div>
                                                                 {product.price > BILLING.SHIPPING.FREE_THRESHOLD && (
                                                                     <div className="flex flex-col items-end">
                                                                         <span className="material-symbols-outlined text-green-500 text-2xl">local_shipping</span>
                                                                         <span className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wide">Envío Gratis</span>
                                                                     </div>
                                                                 )}
                                                             </div>
                                                         )}                    </div>
                             <div className="h-px w-full bg-black/5 dark:bg-white/5"></div>
                             <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0"><span className="material-symbols-outlined text-xl">credit_card</span></div><div><p className="text-sm font-medium">3 cuotas sin interés de <span className="font-bold">$ {installmentPrice}</span></p><p className="text-[10px] text-text-secondary dark:text-gray-400">Con todas las tarjetas bancarias</p></div></div>
                         </div>

                         <section className="mb-8">
                             <h3 className="text-lg font-bold mb-3">Sobre el producto</h3>
                             <div className="text-text-secondary dark:text-gray-300 text-sm leading-relaxed space-y-4">
                                 <p>{product.description}</p>
                                 <div className="grid grid-cols-2 gap-3 mt-4"><div className="bg-white dark:bg-surface-dark p-3 rounded-xl border border-black/5 dark:border-white/5"><span className="block text-[10px] uppercase font-bold text-text-secondary dark:text-gray-500 mb-1">Garantía</span><span className="text-sm font-bold">{product.condition !== 'new' ? '3 meses' : '6 meses oficial'}</span></div><div className="bg-white dark:bg-surface-dark p-3 rounded-xl border border-black/5 dark:border-white/5"><span className="block text-[10px] uppercase font-bold text-text-secondary dark:text-gray-500 mb-1">Stock</span><span className="text-sm font-bold">{product.stock} unidades</span></div></div>
                             </div>
                         </section>
                          
                            <ProductQuestions
                                productId={product.id}
                                shopId={product.shopId}
                                partnerId={product.partnerId}
                            />

                            <ProductReviews
                                productId={product.id}
                                shopId={product.shopId}
                                partnerId={product.partnerId}
                            />

                            {shop && (
                                <div className="mt-6 mb-4 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/tienda/${shop.slug}`)}>
                                            {shop.assets?.logo ? (
                                                <img src={shop.assets.logo} alt={`Logo de ${shop.name}`} className="w-12 h-12 rounded-lg object-cover" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg bg-orange-200 dark:bg-orange-800 flex items-center justify-center">
                                                    <span className="material-symbols-outlined text-orange-600 dark:text-orange-400">storefront</span>
                                                </div>
                                            )}
                                            <div>
                                                <h3 className="font-bold text-text-main dark:text-white flex items-center gap-1">
                                                    {shop.name}
                                                </h3>
                                                {/* Store Badge Group - Shows store type, performance and trust badges */}
                                                <div className="mt-1">
                                                    <StoreBadgeGroup
                                                        storeType={shop.id === 'alltuc-official' ? 'official' : (shop.isVerified ? 'verified' : 'basic')}
                                                        isNew={shop.stats?.totalSales ? shop.stats.totalSales < 10 : false}
                                                        isVerified={shop.isVerified}
                                                        hasFastShipping={shop.stats?.totalSales ? shop.stats.totalSales > 50 : false}
                                                        isTopRated={shop.stats?.rating ? shop.stats.rating >= 4.5 : false}
                                                        hasGuarantee={shop.isVerified}
                                                        showPerformance={true}
                                                        showTrust={true}
                                                        size="sm"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-1 text-xs text-text-secondary mt-1">
                                                    <span className="material-symbols-outlined text-yellow-500 text-sm filled">star</span>
                                                    <span>{(shop.stats?.rating || 0).toFixed(1)}</span>
                                                    <span className="text-gray-400">({shop.stats?.reviewCount || 0} reseñas)</span>
                                                </div>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => navigate(`/tienda/${shop.slug}`)}
                                            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 transition-colors"
                                        >
                                            Ver Tienda
                                        </button>
                                    </div>
                                </div>
                            )}

                           {shopProducts.length > 0 && (
                               <div className="mb-6 -mx-6">
                                   <HorizontalProductList 
                                       title="Más de esta tienda" 
                                       subtitle={`Otros productos de ${shop?.name || 'la tienda'}`}
                                       products={shopProducts} 
                                       isLoading={false} 
                                       seeAllLink={shop ? `/tienda/${shop.slug}` : ''}
                                       bgColor="bg-orange-50 dark:bg-orange-900/10"
                                       titleColor="text-orange-600 dark:text-orange-400"
                                       icon="storefront"
                                   />
                               </div>
                           )}

                           <div className="h-px w-full bg-black/5 dark:bg-white/5 mb-6"></div>
                           <section className="mb-4 -mx-6"><HorizontalProductList title="Quizás te interese" products={relatedProducts} isLoading={false} seeAllLink="" /></section>
                     </main>

                     <ProductStickyBar 
                         price={product.isPro ? clubPrice : (product.price || 0)}
                         quantity={quantity}
                         stock={product.stock || 0}
                         setQuantity={setQuantity}
                         onAdd={handleAddToCart}
                         isAdded={isAdded}
                         isProLocked={isProLocked}
isOutOfStock={isOutOfStock}
                          addToast={addToast}
                      />
                  </article>
              </>
          );
      }
  };